import { useRef, useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * Drag-and-drop image uploader for the admin panel.
 * - Accepts PNG, JPEG, WEBP, GIF up to 5 MB
 * - Converts file to base64, POSTs to /api/admin/upload-image
 * - Calls `onUploaded(url)` with the persistent /api/images/{id} URL
 */
export default function ImageUploader({ onUploaded, label = "Glissez une image ici", testId }) {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|gif)$/i.test(file.type)) {
      toast.error("Format non supporté (PNG, JPG, WEBP, GIF uniquement)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop lourde (max 5 Mo)");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const r = await api.post("/admin/upload-image", {
        data: dataUrl,
        filename: file.name,
      });
      toast.success(`Image uploadée (${Math.round((r.data.size || 0) / 1024)} Ko)`);
      onUploaded?.(r.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      data-testid={testId || "image-uploader"}
      className={`group cursor-pointer border-2 border-dashed transition-all px-4 py-6 flex items-center justify-center gap-3 text-xs uppercase tracking-widest font-bold ${
        drag
          ? "border-white bg-white/10 text-white"
          : "border-white/30 text-white/70 hover:border-white/60 hover:text-white"
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {uploading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Envoi en cours…</span>
        </>
      ) : (
        <>
          <Upload className="w-4 h-4" />
          <span>{label}</span>
        </>
      )}
    </div>
  );
}

/**
 * Small preview thumb with an X to remove.
 */
export function ImageThumb({ url, onRemove }) {
  if (!url) return null;
  return (
    <div className="relative inline-block">
      <img
        src={url}
        alt=""
        className="w-20 h-20 object-cover border border-white/20"
      />
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute -top-2 -right-2 bg-white text-[#001F3F] rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-500 hover:text-white transition"
          data-testid="image-thumb-remove"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
